import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["value", "onChange", "timezone", "labelledById", "minuteStep"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.number.is-finite.js";
import "core-js/modules/es.number.is-nan.js";
import "core-js/modules/es.number.parse-int.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.exec.js";
import "core-js/modules/es.string.pad-start.js";
import "core-js/modules/es.string.replace.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { toTimeStr } from 'utils/times/toTimeStr';
import { clamp } from 'utils/ui';
import { useCustomControl } from 'components/FormField';
import Flex, { Box } from 'components/Flex';
import Icon from 'components/Icon';
import { StyledInput } from 'components/TextInput';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var VALID_MINUTE_STEPS = [1, 2, 3, 4, 5, 6, 10, 12, 15, 20, 30];
function getClosestValidMinuteStep(step) {
  return VALID_MINUTE_STEPS.reduce(function (closest, candidate) {
    return Math.abs(candidate - step) < Math.abs(closest - step) ? candidate : closest;
  }, VALID_MINUTE_STEPS[0]);
}
function normalizeStep(step) {
  // Keep existing behavior stable if the incoming prop is NaN/Infinity.
  if (!Number.isFinite(step)) {
    return 30;
  }

  // Restrict to divisors of 60 so arrow stepping and blur snapping are predictable.
  var rounded = Math.round(step);
  var clamped = clamp(rounded, 1, 59);
  return getClosestValidMinuteStep(clamped);
}
function getNumericSegmentValue(value) {
  return value.replace(/\D+/g, '').slice(0, 2);
}
function parseTime(value) {
  var _parts$, _parts$2;
  var parts = (value !== null && value !== void 0 ? value : '').split(':');
  var h = Number.parseInt((_parts$ = parts[0]) !== null && _parts$ !== void 0 ? _parts$ : '');
  var m = Number.parseInt((_parts$2 = parts[1]) !== null && _parts$2 !== void 0 ? _parts$2 : '');
  return [Number.isNaN(h) ? 0 : clamp(h, 0, 23), Number.isNaN(m) ? 0 : clamp(m, 0, 59)];
}
var ArrowButtonBase = styled.button.withConfig({
  displayName: "TimePicker__ArrowButtonBase",
  componentId: "sc-kgleqm-0"
})(["display:flex;align-items:center;justify-content:center;width:100%;padding:2px;background:none;border:none;cursor:pointer;color:inherit;opacity:0.2;transition:opacity 100ms;touch-action:manipulation;&:focus-visible{opacity:1;outline:2px solid currentColor;outline-offset:1px;}"]);

// CSS grid: 3 cols (hours | separator | minutes), 3 rows (arrow-up | input | arrow-down).
// The separator is placed in row 2 so it aligns with the inputs, not the arrows.
// Arrows are in the normal document flow so they don't bleed into adjacent form rows.
var TimePickerGrid = styled.div.withConfig({
  displayName: "TimePicker__TimePickerGrid",
  componentId: "sc-kgleqm-1"
})(["display:inline-grid;grid-template-columns:auto auto auto;grid-template-rows:auto auto auto;align-items:center;", ":hover{opacity:1;}"], ArrowButtonBase);
var SegmentWrapper = styled.div.withConfig({
  displayName: "TimePicker__SegmentWrapper",
  componentId: "sc-kgleqm-2"
})(["display:contents;&:hover ", ",&:focus-within ", "{opacity:0.5;}&:hover ", ":hover,&:focus-within ", ":hover{opacity:1;}"], ArrowButtonBase, ArrowButtonBase, ArrowButtonBase, ArrowButtonBase);
var Separator = styled.span.withConfig({
  displayName: "TimePicker__Separator",
  componentId: "sc-kgleqm-3"
})(["padding:0 1px;user-select:none;pointer-events:none;"]);

// Returns props shared by all four arrow buttons.
// onMouseDown + onTouchStart both call preventDefault so the focused segment
// input does not blur before the handler fires (desktop and iOS respectively).
// onTouchEnd is needed on iOS because preventDefault on touchstart suppresses
// the synthetic click event.
function arrowButtonProps(handler, ariaLabel) {
  return {
    type: 'button',
    tabIndex: -1,
    'aria-label': ariaLabel,
    onMouseDown: function onMouseDown(e) {
      return e.preventDefault();
    },
    onTouchStart: function onTouchStart(e) {
      return e.preventDefault();
    },
    onClick: handler,
    onTouchEnd: function onTouchEnd(e) {
      e.preventDefault();
      handler();
    }
  };
}
function handleSegmentKeyDown(e, increase, decrease) {
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    increase();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    decrease();
  }
}
function parseRawSegment(rawValue, fallback, min, max) {
  var parsed = Number.parseInt(rawValue);
  return Number.isNaN(parsed) ? fallback : clamp(parsed, min, max);
}
function TimePicker(_ref) {
  var value = _ref.value,
    onChange = _ref.onChange,
    timezone = _ref.timezone,
    labelledById = _ref.labelledById,
    _ref$minuteStep = _ref.minuteStep,
    minuteStep = _ref$minuteStep === void 0 ? 30 : _ref$minuteStep,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  useCustomControl();
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var timezoneId = useId();
  var step = normalizeStep(minuteStep);
  var _parseTime = parseTime(value),
    _parseTime2 = _slicedToArray(_parseTime, 2),
    hours = _parseTime2[0],
    minutes = _parseTime2[1];
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    rawHours = _useState2[0],
    setRawHours = _useState2[1];
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    rawMinutes = _useState4[0],
    setRawMinutes = _useState4[1];
  var hoursRef = useRef(null);
  var minutesRef = useRef(null);
  var displayHours = rawHours !== null && rawHours !== void 0 ? rawHours : String(hours).padStart(2, '0');
  var displayMinutes = rawMinutes !== null && rawMinutes !== void 0 ? rawMinutes : String(minutes).padStart(2, '0');
  function applyHoursArrow(direction) {
    // +24 before % 24 keeps the result positive when direction=-1 and hours=0
    onChange(toTimeStr((hours + direction + 24) % 24, minutes));
  }
  function applyMinutesArrow(direction) {
    // When already aligned, step normally. When not aligned, floor/ceil to
    // the adjacent interval in the arrow direction rather than round-then-step,
    // which would skip an interval (e.g. 44 → snap(30) − 30 = 0, skipping 30).
    var aligned = minutes % step === 0;
    // raw can be negative (back across hour boundary) or ≥60 (forward across)
    var raw = aligned ? minutes + direction * step : direction === 1 ? Math.ceil(minutes / step) * step : Math.floor(minutes / step) * step;
    onChange(toTimeStr((hours + Math.floor(raw / 60) + 24) % 24,
    // hour delta: -1, 0, or +1
    (raw % 60 + 60) % 60 // positive modulo — JS % returns negative for negative operands
    ));
  }
  function handleHoursKeyDown(e) {
    handleSegmentKeyDown(e, function () {
      return applyHoursArrow(1);
    }, function () {
      return applyHoursArrow(-1);
    });
  }
  function handleMinutesKeyDown(e) {
    handleSegmentKeyDown(e, function () {
      return applyMinutesArrow(1);
    }, function () {
      return applyMinutesArrow(-1);
    });
  }
  function handleHoursBlur() {
    if (rawHours === null) return;
    var clamped = parseRawSegment(rawHours, hours, 0, 23);
    setRawHours(null);
    onChange(toTimeStr(clamped, minutes));
  }
  function handleMinutesBlur() {
    if (rawMinutes === null) return;
    var clamped = parseRawSegment(rawMinutes, minutes, 0, 59);
    var snapped = Math.round(clamped / step) * step;
    setRawMinutes(null);
    // snapped can be 60 when the typed value is closer to the next hour (e.g. 50
    // with step=30 rounds to 60). Roll over rather than clamping back to 30.
    if (snapped >= 60) {
      onChange(toTimeStr((hours + 1) % 24, 0));
    } else {
      onChange(toTimeStr(hours, snapped));
    }
  }
  return /*#__PURE__*/_jsxs(Flex, _objectSpread(_objectSpread({
    align: "center",
    role: "group",
    "aria-labelledby": labelledById,
    "aria-describedby": timezone ? timezoneId : undefined
  }, otherProps), {}, {
    children: [/*#__PURE__*/_jsxs(TimePickerGrid, {
      children: [/*#__PURE__*/_jsxs(SegmentWrapper, {
        children: [/*#__PURE__*/_jsx(ArrowButtonBase, _objectSpread(_objectSpread({}, arrowButtonProps(function () {
          var _hoursRef$current;
          setRawHours(null);
          applyHoursArrow(1);
          (_hoursRef$current = hoursRef.current) === null || _hoursRef$current === void 0 || _hoursRef$current.focus();
        }, t('actions_increase_hours'))), {}, {
          style: {
            gridColumn: 1,
            gridRow: 1
          },
          children: /*#__PURE__*/_jsx(Icon, {
            name: "nav-up"
          })
        })), /*#__PURE__*/_jsx(StyledInput, {
          ref: hoursRef,
          role: "spinbutton",
          inputMode: "numeric",
          pattern: "[0-9]*",
          size: 2,
          "aria-label": t('time_hour_plural'),
          "aria-valuemin": 0,
          "aria-valuemax": 23,
          "aria-valuenow": hours,
          value: displayHours,
          style: {
            gridColumn: 1,
            gridRow: 2,
            textAlign: 'center'
          },
          onFocus: function onFocus(e) {
            return e.currentTarget.select();
          },
          onChange: function onChange(e) {
            return setRawHours(getNumericSegmentValue(e.target.value));
          },
          onKeyDown: handleHoursKeyDown,
          onBlur: handleHoursBlur
        }), /*#__PURE__*/_jsx(ArrowButtonBase, _objectSpread(_objectSpread({}, arrowButtonProps(function () {
          var _hoursRef$current2;
          setRawHours(null);
          applyHoursArrow(-1);
          (_hoursRef$current2 = hoursRef.current) === null || _hoursRef$current2 === void 0 || _hoursRef$current2.focus();
        }, t('actions_decrease_hours'))), {}, {
          style: {
            gridColumn: 1,
            gridRow: 3
          },
          children: /*#__PURE__*/_jsx(Icon, {
            name: "nav-down"
          })
        }))]
      }), /*#__PURE__*/_jsx(Separator, {
        style: {
          gridColumn: 2,
          gridRow: 2
        },
        "aria-hidden": "true",
        children: ":"
      }), /*#__PURE__*/_jsxs(SegmentWrapper, {
        children: [/*#__PURE__*/_jsx(ArrowButtonBase, _objectSpread(_objectSpread({}, arrowButtonProps(function () {
          var _minutesRef$current;
          setRawMinutes(null);
          applyMinutesArrow(1);
          (_minutesRef$current = minutesRef.current) === null || _minutesRef$current === void 0 || _minutesRef$current.focus();
        }, t('actions_increase_minutes'))), {}, {
          style: {
            gridColumn: 3,
            gridRow: 1
          },
          children: /*#__PURE__*/_jsx(Icon, {
            name: "nav-up"
          })
        })), /*#__PURE__*/_jsx(StyledInput, {
          ref: minutesRef,
          role: "spinbutton",
          inputMode: "numeric",
          pattern: "[0-9]*",
          size: 2,
          "aria-label": t('time_minute_plural'),
          "aria-valuemin": 0,
          "aria-valuemax": 59,
          "aria-valuenow": minutes,
          value: displayMinutes,
          style: {
            gridColumn: 3,
            gridRow: 2,
            textAlign: 'center'
          },
          onFocus: function onFocus(e) {
            return e.currentTarget.select();
          },
          onChange: function onChange(e) {
            return setRawMinutes(getNumericSegmentValue(e.target.value));
          },
          onKeyDown: handleMinutesKeyDown,
          onBlur: handleMinutesBlur
        }), /*#__PURE__*/_jsx(ArrowButtonBase, _objectSpread(_objectSpread({}, arrowButtonProps(function () {
          var _minutesRef$current2;
          setRawMinutes(null);
          applyMinutesArrow(-1);
          (_minutesRef$current2 = minutesRef.current) === null || _minutesRef$current2 === void 0 || _minutesRef$current2.focus();
        }, t('actions_decrease_minutes'))), {}, {
          style: {
            gridColumn: 3,
            gridRow: 3
          },
          children: /*#__PURE__*/_jsx(Icon, {
            name: "nav-down"
          })
        }))]
      })]
    }), timezone && /*#__PURE__*/_jsx(Box, {
      id: timezoneId,
      fontSize: "s",
      ml: "xxs",
      children: timezone
    })]
  }));
}
export default TimePicker;